import type { ThemePref } from "./types";

/**
 * Resolves the user's theme preference against the current system setting.
 * Returns "dark" or "light".
 */
export function resolveTheme(pref: ThemePref): "dark" | "light" {
  if (pref === "system") {
    // jsdom + some restricted browsers don't expose matchMedia — default to dark.
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "dark";
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return pref;
}

/**
 * Apply the resolved theme to <html>. Adds `.dark` or `.light` (mutually
 * exclusive). CSS variables in index.css key off the class.
 */
export function applyTheme(pref: ThemePref): void {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(pref);
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.classList.toggle("light", resolved === "light");
}

/**
 * Subscribe to system theme changes so "system" preference updates live.
 * Returns the unsubscribe function.
 */
export function subscribeToSystemTheme(pref: () => ThemePref, onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mq = window.matchMedia("(prefers-color-scheme: light)");
  const handler = () => {
    if (pref() === "system") onChange();
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
